import { gql } from '@apollo/client';

const creditProfileAuthStatusProfileFragment = gql`
  fragment CreditProfileAuthStatusProfileFragment on CreditProfileAuthStatusProfile {
    status
    authenticatedAt
    activatedAt
    deactivatedAt
    reactivatedAt
    blockedAt
    questions {
      id
      type
      text
      sequence
      answers {
        id
        text
        sequence
      }
    }
    inOtpFlow
    hasThinFile
    thinFileReason
    isDeceased
    notEnoughData
    numPullsInPast24Hours
    tuId
    previousStatus
    message
    description
  }
`;

const tuCreditReportIdentityProfileFragment = gql`
  fragment TuCreditReportIdentityProfileFragment on TuCreditReportIdentityProfile {
    id
    inquiryDate
    safetyCheckPassed
    hasReportErrors
    isStale
    reportPulledAt
    reportPulledAtIterableDatetime
    nextReportPullAt
    vantageScore3
    vantageScore3Bucket
    vantageScore3Bin
    vantageScore3Bin7Days
    vantageScore3Bin28Days
    creditScorePopulationRank
    vantageScore3ChangeDirection7Days
    vantageScore3ChangeDirection28Days
    vantageScore3ChangeDirection
    vantageScore3Delta
    vantageScore3Delta7Days
    vantageScore3Delta28Days
    vantageScore3DeltaBucket
    numAccounts
    numOpenAccounts
    numClosedAccounts
    numInquiries
    numRecentInquiries
    numDerogatoryAccounts
    numDelinquentAccounts
    numPublicRecords
    percentUtilization
    percentOnTimePayments
    sumAgeOfCredit
    sumAvailableCredit
    sumBalances
    sumMonthlyPayments
    percentUtilizationDelta
    percentOnTimePaymentsDelta
    sumAgeOfCreditDelta
    numOpenAccountsDelta
    sumRevolvingDebtBalances
    sumAssetBackedDebtBalances
    numCreditCardAccounts
    sumCreditCardBalances
    sumCreditCardLimits
    numStudentLoanAccounts
    sumStudentLoanBalances
    numAutoLoanAccounts
    sumAutoLoanBalances
    numPersonalLoanAccounts
    sumPersonalLoanBalances
    numMortgageAccounts
    sumMortgageBalances
    numOtherLoanAccounts
    sumOtherLoanBalances
    numHelocAccounts
    sumHelocBalances
    numUtilityAccounts
    sumUtilityBalances
    numOtherAccounts
    sumOtherBalances
    numMissedPayments
    vantageScore3DeltaSinceReg
    avgAgeOfCredit
    nwSumBalancesOpenRevolvingLineOfCredit
    nwSumCreditLimitOpenRevolvingLineOfCredit
    nwPercentUtilization
    nwNumMissedPaymentsRecent
    nwAvgAgeOfCreditOpenMonths
    nwSumBalancesOpen
    nwNumMissedPaymentsRecentBucket
    nwPercentUtilizationBucket
    nwAvgAgeOfCreditOpenBucket
    nwNumOntimeAccounts
    nwPercentUtilization28Days
    nwNumOntimeAccounts28Days
    numAccounts28Days
    numDerogatoryAccounts28Days
    numOpenAccounts28Days
    numClosedAccounts28Days
    nwSumBalancesOpen28Days
    nwSumCreditLimitOpenRevolvingLineOfCredit28Days
    numInquiries28Days
    nwAvgAgeOfCreditOpenMonths28Days
    nwPercentUtilizationDelta28Days
    nwNumOntimeAccountsDelta28Days
    numAccountsDelta28Days
    numDerogatoryAccountsDelta28Days
    numOpenAccountsDelta28Days
    numClosedAccountsDelta28Days
    nwSumBalancesOpenDelta28Days
    nwSumCreditLimitOpenRevolvingLineOfCreditDelta28Days
    numInquiriesDelta28Days
    nwAvgAgeOfCreditOpenMonthsDelta28Days
    currentAddress {
      addressLine1
      addressLine2
      city
      state
      zipCode
      longitude
      latitude
      isValidated
      validatedAt
      validatedBy
    }
    inquiries {
      subscriberName
      inquiryType
      bureau
      inquiredAt
      industry
      compositeKey
    }
    tradelines {
      accountType
      accountTypeSymbol
      accountSubtype
      accountSubtypeSymbol
      accountNumberLast4
      accountDesignator
      accountCondition
      openClosed
      openClosedSymbol
      highBalance
      dateReportedAt
      dateOpenedAt
      dateClosedAt
      dateVerifiedAt
      dateAccountStatus
      currentBalance
      creditorName
      bureau
      creditLimit
      disputeFlag
      payStatus
      payStatusDescription
      remark
      paymentFrequency
      worstPayStatus
      worstPayStatusSymbol
      amountPastDue
      collateral
      lastPaymentDate
      late30Count
      late60Count
      late90Count
      monthlyPayment
      monthsReviewed
      termMonths
      worstPayStatusCount
      payHistoryStatus
      payHistoryStartDate
      payHistoryBreakDown {
        status
        date
      }
      accountNumberHash
      creditType
      compositeKey
      nwCreditLimitNormalized
      nwUtilizationPct
      nwDateOpenedAtNormalized
      nwMonthsOpened
      derogatoryTradelineReason
      payStatusSymbol
      nwPayStatusBucket
      nwLastMissedPaymentDate
      isIncludedInUtilization
    }
    employers {
      name
      dateReportedAt
      dateUpdatedAt
    }
    publicRecords {
      courtName
      filedAt
      referenceNumber
      subscriberCode
      bureau
      classification
      status
      type
    }
  }
`;

export const INITIALIZE_CREDIT_PROFILE_MUTATION = gql`
  mutation InitializeCreditProfile($customer: CreditProfileAuthCustomerInput!) {
    initializeCreditProfile(customer: $customer) {
      ...CreditProfileAuthStatusProfileFragment
    }
  }
  ${creditProfileAuthStatusProfileFragment}
`;

export const AUTHENTICATE_CREDIT_PROFILE_QUESTIONS_MUTATION = gql`
  mutation AuthenticateCreditProfileQuestions(
    $answers: [CreditProfileAuthAnswerInput!]!
    $resetPasscode: Boolean
  ) {
    authenticateCreditProfileQuestions(
      answers: $answers
      resetPasscode: $resetPasscode
    ) {
      ...TuCreditReportIdentityProfileFragment
      ...CreditProfileAuthStatusProfileFragment
    }
  }
  ${tuCreditReportIdentityProfileFragment}
  ${creditProfileAuthStatusProfileFragment}
`;

export const CREATE_TRUST_SESSION_ID_MUTATION = gql`
  mutation CreateTrustSessionId($blackBox: String!) {
    createTrustSessionId(input: { blackBox: $blackBox }) {
      success
    }
  }
`;

export const TU_AUTHENTICATION_STATUS_PROFILE_QUERY = gql`
  query TuAuthenticationStatusProfile {
    tuAuthenticationStatusProfile {
      ...CreditProfileAuthStatusProfileFragment
    }
  }
  ${creditProfileAuthStatusProfileFragment}
`;

export const ENROLL_CREDIT_PROFILE_MUTATION = gql`
  mutation EnrollCreditProfile {
    enrollCreditProfile {
      ...TuCreditReportIdentityProfileFragment
    }
  }
  ${tuCreditReportIdentityProfileFragment}
`;
